import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCode, faImage, faPaintbrushPencil, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { Flex, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import { Icon, Text, UniversalSelect } from '@feathr/components';

import type { ICreativesFilters, TCreativeTypeFilter } from '../ProjectCreativesPage.types';
import { getChannelClasses, getTypeClasses } from '../ProjectCreativesPage.utils';

import * as styles from './ProjectCreativeTypeSelect.css';

interface IProps {
  filters: ICreativesFilters;
  setFilters: (filters: ICreativesFilters) => void;
}

interface ICreativeTypeOption {
  id: TCreativeTypeFilter;
  name: string;
  icon: IconDefinition;
  isDisabled?: boolean;
}

function getOptions(t: (key: string) => string): ICreativeTypeOption[] {
  return [
    {
      id: 'bannersnack',
      name: t('Bannersnack'),
      icon: faPaintbrushPencil,
    },
    {
      id: 'ad_tag',
      name: t('Ad Tag'),
      icon: faCode,
    },
    {
      id: 'image',
      name: t('Image'),
      icon: faImage,
    },
    {
      id: 'video',
      name: t('Video'),
      icon: faVideo,
    },
  ];
}

function CreativeTypeOption(props: OptionProps<ICreativeTypeOption>): JSX.Element {
  const { data } = props;
  const theme = useMantineTheme();
  return (
    <components.Option {...props}>
      <Flex align={'center'} gap={theme.spacing['2']}>
        <Icon icon={data.icon} size={4} theme={'sky'} />
        <Text.DefaultRegular>{data.name}</Text.DefaultRegular>
      </Flex>
    </components.Option>
  );
}

function CreativeTypeSingleValue(props: SingleValueProps<ICreativeTypeOption>): JSX.Element {
  const { data } = props;
  const theme = useMantineTheme();
  return (
    <components.SingleValue {...props}>
      <Flex align={'center'} gap={theme.spacing['2']}>
        <Icon icon={data.icon} size={4} theme={'sky'} />
        <Text.DefaultMedium>{data.name}</Text.DefaultMedium>
      </Flex>
    </components.SingleValue>
  );
}

function ProjectCreativeTypeSelect({ filters, setFilters }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const baseOptions = getOptions(t);
  const channelClasses = getChannelClasses(filters.channel);

  // Add isDisabled to options based on channel filter
  const options = baseOptions.map((option) => {
    const typeClasses = getTypeClasses(option.id);
    return {
      ...option,
      // Disable if channel is selected and no intersection with option classes
      isDisabled:
        channelClasses && typeClasses
          ? !typeClasses.some((cls) => channelClasses.includes(cls))
          : false,
    };
  });

  const selectedOption = options.find((option) => option.id === filters.creativeType);

  function handleSelect(option: ICreativeTypeOption): void {
    setFilters({
      ...filters,
      creativeType: option.id,
    });
  }

  function handleClear(): void {
    setFilters({
      ...filters,
      creativeType: undefined,
    });
  }

  return (
    <UniversalSelect<ICreativeTypeOption>
      components={{ Option: CreativeTypeOption, SingleValue: CreativeTypeSingleValue }}
      isClearable={true}
      name={'creative-type-filter'}
      onClear={handleClear}
      onSelectSingle={handleSelect}
      options={options}
      placeholder={t('Type')}
      value={selectedOption}
      wrapperClassName={styles.filter}
    />
  );
}

export default ProjectCreativeTypeSelect;
